import { createAsyncThunk } from '@reduxjs/toolkit';
import { ClinicOwnerActions } from 'applicaiton/constants/actions';
import { AxiosError } from 'axios';
import { ClinicOwnerAPI } from 'integration/api/clinicOwner';
import {
  ClinicOwnerUpdateDto,
  SupportedLanguageIso,
  UserChangeEmailDto,
  UserChangePhoneDto,
  UserRequestChangePhoneDto,
  UserVerifyCodeDto,
} from '@docbay/schemas';
import { getToken } from 'applicaiton/sessionStorage/auth';
import { UsersAPI } from 'integration/api/users';
import { updatePermissionsByClinicOwnerIdProps } from './models';
import { fetchClinicOwnerStaffProps } from 'common/types/clinicOwner';
import { getLanguageFromLocalStorage } from 'applicaiton/sessionStorage/language';
import { getRegistrationToken } from 'applicaiton/sessionStorage/clinicRegistration';

/**
 * Fetches a clinic owner by their ID.
 *
 * @param id - The ID of the clinic owner to fetch.
 * @returns A promise that resolves with the fetched clinic owner data.
 */
export const getClinicOwnerById = createAsyncThunk(
  ClinicOwnerActions.getClinicOwnerById,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerAPI.getClinicOwnerById(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Updates a clinic owner's information.
 *
 * @param params - An object containing the ID of the clinic owner to update and the new data.
 * @returns A promise that resolves with the updated clinic owner data.
 */
export const updateClinicOwner = createAsyncThunk(
  ClinicOwnerActions.updateClinicOwner,
  async (
    params: { id: string; data: ClinicOwnerUpdateDto },
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ClinicOwnerAPI.updateClinicOwner(
        params.id,
        params.data,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Verifies an old email for a clinic owner.
 *
 * @param params - An object containing the old email to verify.
 * @returns A promise that resolves with the verification data.
 */
export const clinicOwnerVerifyOldEmail = createAsyncThunk(
  ClinicOwnerActions.clinicOwnerVerifyOldEmail,
  async (params: { email: string }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const language = getLanguageFromLocalStorage();
      const { data } = await UsersAPI.userVerifyOldEmail(
        { ...params, language },
        token,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Verifies a code for a clinic owner.
 *
 * @param params - An object containing the verification code to verify.
 * @returns A promise that resolves with the verification data.
 */
export const clinicOwnerVerifyCode = createAsyncThunk(
  ClinicOwnerActions.clinicOwnerVerifyCode,
  async (params: UserVerifyCodeDto, { rejectWithValue }) => {
    try {
      // const token = getToken();
      const { data } = await UsersAPI.usersVerifyCode(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Sets a new email for a clinic owner.
 *
 * @param params - An object containing the new email to set.
 * @returns A promise that resolves with the updated email data.
 */
export const clinicOwnerSetNewEmail = createAsyncThunk(
  ClinicOwnerActions.clinicOwnerSetNewEmail,
  async (params: UserChangeEmailDto, { rejectWithValue }) => {
    try {
      const token = getToken();
      const { data } = await UsersAPI.userSetNewEmail(params, token);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the permissions for a clinic owner by their ID.
 *
 * @param id - The ID of the clinic owner to fetch permissions for.
 * @returns A promise that resolves with the fetched permission data.
 */
export const getPermissionsByClinicOwnerId = createAsyncThunk(
  ClinicOwnerActions.getPermissionsByClinicOwnerId,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerAPI.getPermissionsByClinicOwnerId(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Updates the permissions for a clinic owner by their ID.
 *
 * @param params - An object containing the ID of the clinic owner to update permissions for and the new permissions.
 * @returns A promise that resolves with the updated permission data.
 */
export const updatePermissionsByClinicOwnerId = createAsyncThunk(
  ClinicOwnerActions.updatePermissionsByClinicOwnerId,
  async (
    { id, permission }: updatePermissionsByClinicOwnerIdProps,
    { rejectWithValue },
  ) => {
    try {
      const { data } = await ClinicOwnerAPI.updatePermissionsByClinicOwnerId(
        id,
        permission,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the staff for a clinic owner.
 *
 * @param params - An object containing the parameters to fetch the clinic owner's staff.
 * @returns A promise that resolves with the fetched staff data.
 */
export const fetchClinicOwnerStaff = createAsyncThunk(
  ClinicOwnerActions.fetchClinicOwnerStaff,
  async (params: fetchClinicOwnerStaffProps, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerAPI.fetchClinicOwnerStaff(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Verifies a phone number for a clinic owner.
 *
 * @param params - An object containing the request to change a phone number and the language.
 * @returns A promise that resolves with the verification data.
 */
export const clinicOwnerVerifyPhone = createAsyncThunk(
  ClinicOwnerActions.clinicOwnerVerifyPhone,
  async (params: UserRequestChangePhoneDto, { rejectWithValue }) => {
    try {
      const token = getToken();
      const language = getLanguageFromLocalStorage();

      const { data } = await UsersAPI.userPhoneVerificationCode(
        { ...params, language: language as SupportedLanguageIso },
        token,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Sets a new phone number for a clinic owner.
 *
 * @param params - An object containing the new phone number to set.
 * @returns A promise that resolves with the updated phone data.
 */
export const clinicOwnerSetNewPhone = createAsyncThunk(
  ClinicOwnerActions.clinicOwnerSetNewPhone,
  async (params: UserChangePhoneDto, { rejectWithValue }) => {
    try {
      const token = getToken();
      const { data } = await UsersAPI.userSetNewPhone(params, token);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the subscriptions for a clinic owner.
 *
 * @returns A promise that resolves with the fetched subscription data.
 */
export const getSubscriptions = createAsyncThunk(
  ClinicOwnerActions.getSubscriptions,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerAPI.getSubscriptions();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the customer portal link for a clinic owner.
 *
 * @returns A promise that resolves with the fetched customer portal link data.
 */
export const getCustomerPortalLink = createAsyncThunk(
  ClinicOwnerActions.getCustomerPortalLink,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerAPI.getCustomerPortalLink();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the customer billing link for a clinic owner by their ID.
 *
 * @param id - The ID of the clinic owner to fetch the customer billing link for.
 * @returns A promise that resolves with the fetched customer billing link data.
 */
export const getCustomerBillingLink = createAsyncThunk(
  ClinicOwnerActions.getCustomerBillingLink,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerAPI.getCustomerBillingLink(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the customer billing update link for a clinic owner.
 *
 * @returns A promise that resolves with the fetched customer billing update link data.
 */
export const getCustomerBillingUpdateLink = createAsyncThunk(
  ClinicOwnerActions.getCustomerBillingUpdateLink,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerAPI.getCustomerBillingUpdateLink();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the customer billing activation link for a clinic owner.
 *
 * @returns A promise that resolves with the fetched customer billing activation link data.
 */
export const getCustomerBillingActivationLink = createAsyncThunk(
  ClinicOwnerActions.getCustomerBillingActivationLink,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerAPI.getCustomerBillingActivationLink();
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the photo package for a clinic owner.
 *
 * @returns A promise that resolves with the fetched photo package data.
 */
export const getPhotoPackage = createAsyncThunk(
  ClinicOwnerActions.getPhotoPackage,
  async (_, { rejectWithValue }) => {
    try {
      const token = getRegistrationToken();
      const { data } = await ClinicOwnerAPI.getPhotoPackage(token);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the account status for a clinic owner by their ID.
 *
 * @param id - The ID of the clinic owner to fetch the account status for.
 * @returns A promise that resolves with the fetched account status data.
 */
export const getAccountStatus = createAsyncThunk(
  ClinicOwnerActions.getAccountStatus,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerAPI.getAccountStatus(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes a clinic owner account by their ID.
 *
 * @param id - The ID of the clinic owner to delete the account for.
 * @returns A promise that resolves with the deleted account data.
 */
export const deleteCOAccount = createAsyncThunk(
  ClinicOwnerActions.deleteCOAccount,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ClinicOwnerAPI.deleteCOAccount(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
